import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { pprwAPI, reportsAPI, trainersAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { TrainerAutocomplete } from '../components/TrainerAutocomplete';
import { ContentItemLinkDialog } from '../components/content-items/ContentItemLinkDialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft, Save, Copy, FileText, Loader2, Calendar, Clock,
  Globe, User, Users, BookOpen, FlaskConical, CheckCircle2,
  AlertCircle, XCircle, Clock3, DollarSign, FileCheck, MessageSquare, Layers, X
} from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────────────────
const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];
const TIMEZONES = ['IST', 'BST', 'GMT', 'PST', 'PDT'];
const DELIVERY_TYPES = ['Live', 'Simu Live'];
const DELIVERY_STATUSES = ['Scheduled', 'Upcoming', 'In Progress', 'Completed', 'On Hold', 'Cancelled'];
const CONTENT_STATUSES = ['Not Started', 'In Progress', 'Ready', 'Shared', 'Delayed'];
const LAB_STATUSES = ['Not Started', 'In Progress', 'Live', 'Shared', 'Delayed'];
const READINESS_STATUSES = ['Not Started', 'In Progress', 'Ready', 'Shared', 'Delayed'];
const CONTRACT_STATUSES = ['To Be Initiated', 'Initiated', 'Signed'];
const SHARE_STATUSES = ['To be shared', 'Shared'];
const EXEC_CALL_STATUSES = ['Pending', 'Completed'];
const POE_STATUSES = ['To be submitted', 'Submitted'];
const SOLUTION_AREAS = ['AI Business Solutions', 'Cloud and AI Platforms', 'Security'];
const LABS_TYPES = ['Interactive Simulated Lab', 'Skillable', 'Spektra', 'Hybrid (Skillable + Simulated)', 'Hybrid (Spektra + Simulated)'];

const statusColor = (s) => ({
  Scheduled: 'bg-blue-100 text-blue-700',
  Upcoming: 'bg-purple-100 text-purple-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  Completed: 'bg-emerald-100 text-emerald-700',
  'On Hold': 'bg-orange-100 text-orange-700',
  Cancelled: 'bg-red-100 text-red-700',
}[s] || 'bg-gray-100 text-gray-600');

const readinessColor = (s) => ({
  'Not Started': 'bg-gray-100 text-gray-600',
  'In Progress': 'bg-blue-100 text-blue-700',
  Ready: 'bg-emerald-100 text-emerald-700',
  Shared: 'bg-emerald-100 text-emerald-700',
  Live: 'bg-emerald-100 text-emerald-700',
  Delayed: 'bg-red-100 text-red-700',
}[s] || 'bg-gray-100 text-gray-600');

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const addDays = (dateStr, days) => {
  if (!dateStr) return null;
  try {
    const dt = new Date(dateStr + 'T00:00:00');
    dt.setDate(dt.getDate() + days);
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch (_e) { return null; }
};

// ── Inline field components ───────────────────────────────────────────────────
const WorkshopBadge = () => (
  <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-medium text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded-full align-middle" title="Synced from the parent Workshop">
    <Layers className="w-2.5 h-2.5" />Workshop
  </span>
);

const InlineText = ({ label, value, field, onSave, placeholder = '', type = 'text', testId = '', inherited = false }) => {
  const [editing, setEditing] = useState(false);
  const ref = React.useRef(null);

  const commit = () => {
    const val = ref.current?.value ?? '';
    setEditing(false);
    if (val !== (value || '')) onSave(field, val || null);
  };

  return (
    <div className="group">
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}{inherited && <WorkshopBadge />}</label>
      {editing ? (
        <input type={type} ref={ref} defaultValue={value || ''} autoFocus
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
          className="h-8 text-sm w-full px-3 py-1 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid={testId || `field-${field}`} />
      ) : (
        <p className="text-sm py-1 px-2 rounded cursor-pointer hover:bg-muted/50 min-h-[32px] flex items-center group-hover:bg-muted/30 transition-colors"
          onClick={() => setEditing(true)} data-testid={`display-${field}`}>
          {value || <span className="text-muted-foreground italic">{placeholder || 'Click to edit'}</span>}
        </p>
      )}
    </div>
  );
};

const InlineSelect = ({ label, value, field, options, onSave, testId = '', inherited = false }) => {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}{inherited && <WorkshopBadge />}</label>
      <Select value={value || ''} onValueChange={v => onSave(field, v || null)}>
        <SelectTrigger className="h-8 text-sm" data-testid={testId || `field-${field}`}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>{options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
};

const InlineTextarea = ({ label, value, field, onSave, placeholder = '', testId = '' }) => {
  const ref = React.useRef(null);
  // Sync ref value when external value changes (e.g., after save)
  React.useLayoutEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      ref.current.value = value || '';
    }
  }, [value]);
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      <textarea ref={ref} rows={3} defaultValue={value || ''}
        onBlur={() => {
          const val = ref.current?.value ?? '';
          if (val !== (value || '')) onSave(field, val || null);
        }}
        placeholder={placeholder}
        className="w-full text-sm px-3 py-2 rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-y"
        data-testid={testId || `field-${field}`} />
    </div>
  );
};

// ── Inline Trainer Field (autocomplete + click-to-edit) ──────────────────────
const InlineTrainerField = ({ label, value, field, onSave, trainers = [], placeholder = 'Click to set trainer', testId = '' }) => {
  const [editing, setEditing] = useState(false);

  const commit = (name) => {
    setEditing(false);
    if (name !== (value || '')) onSave(field, name || null);
  };

  return (
    <div className="group">
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      {editing ? (
        <TrainerAutocomplete
          value={value || ''}
          trainers={trainers}
          onCommit={commit}
          autoFocus
          placeholder="Search trainer roster…"
          testId={testId || `field-${field}`}
        />
      ) : (
        <p className="text-sm py-1 px-2 rounded cursor-pointer hover:bg-muted/50 min-h-[32px] flex items-center group-hover:bg-muted/30 transition-colors"
          onClick={() => setEditing(true)} data-testid={`display-${field}`}>
          {value || <span className="text-muted-foreground italic">{placeholder}</span>}
        </p>
      )}
    </div>
  );
};

// ── Readiness Card ────────────────────────────────────────────────────────────
const ReadinessCard = ({ title, icon: Icon, statusField, statusValue, etaField, etaValue, statusOptions, onSave, targetDate }) => {
  const isDelayed = statusValue === 'Delayed';
  const isReady = ['Ready', 'Shared', 'Live'].includes(statusValue);
  return (
    <div className={`rounded-xl border p-4 ${isReady ? 'border-emerald-200 bg-emerald-50/40' : isDelayed ? 'border-red-200 bg-red-50/40' : 'border-border bg-background'}`}
      data-testid={`readiness-${statusField}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${isReady ? 'text-emerald-600' : isDelayed ? 'text-red-500' : 'text-muted-foreground'}`} />
          <span className="text-sm font-medium">{title}</span>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${readinessColor(statusValue)}`}>{statusValue || 'Not Started'}</span>
      </div>
      <InlineSelect label="Status" value={statusValue} field={statusField} options={statusOptions} onSave={onSave} testId={`select-${statusField}`} />
      {targetDate && (
        <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          T-2 Target: <span className="font-medium text-foreground">{targetDate}</span>
        </div>
      )}
      {(isDelayed || etaField) && (
        <div className="mt-2">
          <InlineText label="ETA Date" value={etaValue} field={etaField} onSave={onSave} type="date" testId={`eta-${etaField}`} />
        </div>
      )}
    </div>
  );
};

// ── HTML Report Modal ─────────────────────────────────────────────────────────
function ReportModal({ open, onClose, deliveryId, deliveryTitle }) {
  const [loading, setLoading] = useState(false);
  const [html, setHtml] = useState('');
  const [generated, setGenerated] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.generatePPRWReport(deliveryId);
      setHtml(res.html);
      setGenerated(true);
    } catch (_e) { toast.error('Report generation failed'); }
    finally { setLoading(false); }
  };

  const download = () => {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${deliveryTitle || 'pprw-report'}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => { setHtml(''); setGenerated(false); onClose(); };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="pprw-report-modal">
        <DialogHeader>
          <DialogTitle>AI-Generated Email Report</DialogTitle>
        </DialogHeader>
        {!generated ? (
          <div className="flex flex-col items-center justify-center py-10 gap-4">
            <FileText className="w-12 h-12 text-muted-foreground" />
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              Generate a professional HTML email-ready stakeholder report for this PPRW delivery using AI.
            </p>
            <Button onClick={generate} disabled={loading} data-testid="generate-report-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</> : 'Generate Report'}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-2">
              <Button variant="outline" size="sm" onClick={() => { setGenerated(false); setHtml(''); }}>Regenerate</Button>
              <Button size="sm" onClick={download} className="gap-2" data-testid="download-report-btn">
                <FileText className="w-4 h-4" />Download HTML
              </Button>
            </div>
            <div className="flex-1 overflow-auto border rounded-lg">
              <iframe srcDoc={html} className="w-full h-[500px]" title="Report Preview" data-testid="report-iframe" />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main Detail Page ──────────────────────────────────────────────────────────
export function PPRWDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [highlight, setHighlight] = useState(!!location.state?.highlight);
  useEffect(() => {
    if (!highlight) return;
    const t = setTimeout(() => setHighlight(false), 2500);
    return () => clearTimeout(t);
  }, [highlight]);
  const [delivery, setDelivery] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showReport, setShowReport] = useState(false);
  const [copying, setCopying] = useState(false);
  const [contentItemDialogOpen, setContentItemDialogOpen] = useState(false);
  const [workshop, setWorkshop] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setDelivery(await pprwAPI.get(id)); }
    catch (_e) { toast.error('Delivery not found'); navigate('/pprw'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  const loadTrainers = useCallback(async () => {
    try {
      setTrainers(await trainersAPI.getAll());
    } catch (_e) { /* roster unavailable — autocomplete degrades to free text */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTrainers(); }, [loadTrainers]);
  useEffect(() => {
    if (delivery?.workshop_id) pprwAPI.workshops.get(delivery.workshop_id).then(setWorkshop).catch(() => setWorkshop(null));
    else setWorkshop(null);
  }, [delivery?.workshop_id]);

  const save = async (field, value) => {
    setSaving(true);
    try {
      const updated = await pprwAPI.update(id, { [field]: value });
      setDelivery(updated);
      toast.success('Saved');
    } catch (_e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleUnlinkContentItem = async () => {
    if (!window.confirm('Remove the linked Content Dev course from this delivery?')) return;
    try {
      const updated = await pprwAPI.update(id, { content_item_id: null, content_item_title: null });
      setDelivery(updated);
      toast.success('Content Dev link removed');
    } catch (_e) { toast.error('Failed to unlink'); }
  };

  const handleLinkContentItem = async (item) => {
    setContentItemDialogOpen(false);
    try {
      const updated = await pprwAPI.update(id, { content_item_id: item?.id || null, content_item_title: item?.title || null });
      setDelivery(updated);
      toast.success('Linked to Content Dev course');
    } catch (_e) { toast.error('Failed to link'); }
  };

  const handleCopy = async () => {
    setCopying(true);
    try {
      const copy = await pprwAPI.copy(id);
      toast.success('Delivery copied');
      navigate(`/pprw/${copy.id}`);
    } catch (_e) { toast.error('Copy failed'); }
    finally { setCopying(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!delivery) return null;

  // T-2 calculations
  const t2 = addDays(delivery.start_date, -14);
  const inh = !!delivery.workshop_id;

  const tabs = [
    { id: 'details', label: 'Session Details' },
    { id: 'readiness', label: 'Readiness Tracker' },
    { id: 'execution', label: 'Execution & Post-Delivery' },
    { id: 'financial', label: 'Financial' },
  ];

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="pprw-detail-page">
      {/* Back + Header */}
      <div className={`flex items-start justify-between rounded-lg -m-2 p-2 transition-colors duration-1000 ${highlight ? 'bg-lime/20' : ''}`} data-testid="pprw-detail-header">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/pprw')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold">{delivery.course_title}</h1>
              {delivery.delivery_status && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(delivery.delivery_status)}`}>
                  {delivery.delivery_status}
                </span>
              )}
              {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
              {delivery.series_journey_title && <span>{delivery.series_journey_title}</span>}
              {delivery.quarter && <span>{delivery.quarter}</span>}
              {delivery.time_zone && <span>{delivery.time_zone}</span>}
              {delivery.start_date && <span>{fmtDate(delivery.start_date)}</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleCopy} disabled={copying} className="gap-2" data-testid="copy-delivery-btn">
            {copying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />} Copy
          </Button>
          <Button size="sm" onClick={() => setShowReport(true)} className="gap-2" data-testid="generate-report-btn-header">
            <FileText className="w-4 h-4" /> Report
          </Button>
        </div>
      </div>

      {/* Workshop inheritance banner */}
      {inh && (
        <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 flex items-center justify-between gap-3" data-testid="workshop-inheritance-banner">
          <div className="flex items-center gap-2 text-sm">
            <Layers className="w-4 h-4 text-violet-600 flex-shrink-0" />
            <span>
              Part of workshop{' '}
              <button onClick={() => navigate(`/pprw/workshops/${delivery.workshop_id}`)} className="font-semibold text-violet-700 hover:underline" data-testid="workshop-banner-link">
                {workshop?.title || 'View workshop'}
              </button>. Fields marked <span className="text-violet-600 font-medium">Workshop</span> sync from it.
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate(`/pprw/workshops/${delivery.workshop_id}`)} data-testid="open-workshop-btn">Open Workshop</Button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-0">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab.id ? 'border-lime text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
              data-testid={`detail-tab-${tab.id}`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab: Session Details ─────────────────────────────────────────── */}
      {activeTab === 'details' && (
        <div className="space-y-6" data-testid="tab-details">
          <div className="rounded-xl border border-border bg-background p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">Core Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <InlineText label="Course Title" value={delivery.course_title} field="course_title" onSave={save} testId="field-course-title" />
              </div>
              <div className="col-span-2">
                <InlineText label="Series / Journey Title (optional)" value={delivery.series_journey_title} field="series_journey_title" onSave={save} placeholder="e.g. GEMINI FY26 Depth vILTs" testId="field-series" inherited={inh} />
              </div>
              <InlineSelect label="Quarter" value={delivery.quarter} field="quarter" options={QUARTERS} onSave={save} testId="field-quarter" inherited={inh} />
              <InlineSelect label="Solution Area" value={delivery.solution_area} field="solution_area" options={SOLUTION_AREAS} onSave={save} testId="field-solution-area" inherited={inh} />
              <InlineSelect label="Delivery Type" value={delivery.delivery_type} field="delivery_type" options={DELIVERY_TYPES} onSave={save} testId="field-delivery-type" inherited={inh} />
              <InlineSelect label="Time Zone" value={delivery.time_zone} field="time_zone" options={TIMEZONES} onSave={save} testId="field-time-zone" />
              <InlineText label="Start Date" value={delivery.start_date} field="start_date" onSave={save} type="date" testId="field-start-date" inherited={inh} />
              <InlineText label="End Date" value={delivery.end_date} field="end_date" onSave={save} type="date" testId="field-end-date" inherited={inh} />
              <InlineText label="Start Time" value={delivery.start_time || delivery.session_time} field="start_time" onSave={save} type="time" testId="field-start-time" />
              <InlineText label="End Time" value={delivery.end_time} field="end_time" onSave={save} type="time" testId="field-end-time" />
              <InlineSelect label="Delivery Status" value={delivery.delivery_status} field="delivery_status" options={DELIVERY_STATUSES} onSave={save} testId="field-delivery-status" />
            </div>
          </div>

          <div className="rounded-xl border border-border bg-background p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">Content, Labs and Duration</h2>
            <div className="mb-4">
              <label className="text-xs text-muted-foreground">Linked Content Dev Course</label>
              {delivery.content_item_id ? (
                <div className="flex items-center gap-2 mt-1.5">
                  <button type="button" onClick={() => navigate(`/content-items/${delivery.content_item_id}`)}
                    className="text-sm text-lime-700 hover:underline flex items-center gap-1.5" data-testid="view-linked-content-item-btn">
                    <Layers className="w-3.5 h-3.5" />{delivery.content_item_title || 'View Content Dev Course'}
                  </button>
                  <button type="button" onClick={handleUnlinkContentItem} className="text-muted-foreground hover:text-red-600" title="Unlink Content Dev course" data-testid="unlink-content-item-btn">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="mt-1.5">
                  <Button variant="outline" size="sm" onClick={() => setContentItemDialogOpen(true)} className="gap-2" data-testid="link-content-item-btn">
                    <Layers className="w-3.5 h-3.5" />Link Content Dev Course
                  </Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <InlineText label="No. of Days" value={delivery.num_days != null ? String(delivery.num_days) : ''} field="num_days" onSave={(f, v) => save(f, v ? parseFloat(v) : null)} type="number" testId="field-num-days" inherited={inh} />
              <InlineText label="Total Duration (Hrs.)" value={delivery.duration_hours != null ? String(delivery.duration_hours) : ''} field="duration_hours" onSave={(f, v) => save(f, v ? parseFloat(v) : null)} type="number" testId="field-duration" inherited={inh} />
              <InlineText label="Theory Duration (Hrs.)" value={delivery.theory_duration_hours != null ? String(delivery.theory_duration_hours) : ''} field="theory_duration_hours" onSave={(f, v) => save(f, v ? parseFloat(v) : null)} type="number" testId="field-theory-dur" />
              <InlineText label="Lab Duration (Hrs.)" value={delivery.lab_duration_hours != null ? String(delivery.lab_duration_hours) : ''} field="lab_duration_hours" onSave={(f, v) => save(f, v ? parseFloat(v) : null)} type="number" testId="field-lab-dur" />
              <InlineSelect label="Labs Type" value={delivery.labs_type} field="labs_type" options={LABS_TYPES} onSave={save} testId="field-labs-type" />
              <InlineText label="Lab Capacity" value={delivery.lab_capacity != null ? String(delivery.lab_capacity) : ''} field="lab_capacity" onSave={(f, v) => save(f, v ? parseInt(v) : null)} type="number" testId="field-lab-cap" />
            </div>
          </div>

          <div className="rounded-xl border border-border bg-background p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">Personnel</h2>
            <div className="grid grid-cols-2 gap-4">
              <InlineText label="Content Owner" value={delivery.content_owner} field="content_owner" onSave={save} testId="field-content-owner" inherited={inh} />
              <InlineText label="Lab Owner" value={delivery.lab_owner} field="lab_owner" onSave={save} testId="field-lab-owner" inherited={inh} />
              <InlineTrainerField label="Trainer 1" value={delivery.trainer_1} field="trainer_1" onSave={save} trainers={trainers} testId="field-trainer-1" />
              <InlineTrainerField label="Trainer 2" value={delivery.trainer_2} field="trainer_2" onSave={save} trainers={trainers} testId="field-trainer-2" />
              <InlineText label="Trainer Remarks" value={delivery.trainer_remarks} field="trainer_remarks" onSave={save} placeholder="Notes about trainers" testId="field-trainer-remarks" />
              <InlineText label="Moderators" value={delivery.moderators} field="moderators" onSave={save} testId="field-moderators" />
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Readiness Tracker ─────────────────────────────────────── */}
      {activeTab === 'readiness' && (
        <div className="space-y-6" data-testid="tab-readiness">
          {/* T-2 Info Banner */}
          {t2 && (
            <div className="rounded-xl bg-muted/50 border border-border px-4 py-3 flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">T-2 target date (2 weeks before delivery):</span>
              <span className="font-semibold">{t2}</span>
            </div>
          )}

          {/* Trainer Readiness */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Trainer Readiness</h2>
            <div className="grid grid-cols-2 gap-4">
              <ReadinessCard title="Trainer Contract" icon={FileCheck}
                statusField="trainer_contract_status" statusValue={delivery.trainer_contract_status}
                etaField={null} etaValue={null} statusOptions={CONTRACT_STATUSES} onSave={save} />
              <ReadinessCard title="Trainer Content" icon={BookOpen}
                statusField="trainer_content_status" statusValue={delivery.trainer_content_status}
                etaField={null} etaValue={null} statusOptions={SHARE_STATUSES} onSave={save} targetDate={t2} />
              <ReadinessCard title="Lab Access to Trainer" icon={FlaskConical}
                statusField="lab_access_status" statusValue={delivery.lab_access_status}
                etaField={null} etaValue={null} statusOptions={SHARE_STATUSES} onSave={save} targetDate={t2} />
              <ReadinessCard title="Pre-Execution Call" icon={Users}
                statusField="pre_execution_call_status" statusValue={delivery.pre_execution_call_status}
                etaField={null} etaValue={null} statusOptions={EXEC_CALL_STATUSES} onSave={save} />
            </div>
          </div>

          {/* Content & Platform Readiness */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Content & Platform Readiness</h2>
            <div className="grid grid-cols-2 gap-4">
              <ReadinessCard title="Content Readiness" icon={BookOpen}
                statusField="content_readiness_status" statusValue={delivery.content_readiness_status}
                etaField="content_eta" etaValue={delivery.content_eta} statusOptions={CONTENT_STATUSES} onSave={save} targetDate={t2} />
              <ReadinessCard title="Lab Readiness" icon={FlaskConical}
                statusField="lab_readiness_status" statusValue={delivery.lab_readiness_status}
                etaField="lab_eta" etaValue={delivery.lab_eta} statusOptions={LAB_STATUSES} onSave={save} targetDate={t2} />
              <ReadinessCard title="Polling Qs & LearnPath Links" icon={CheckCircle2}
                statusField="polling_qs_status" statusValue={delivery.polling_qs_status}
                etaField={null} etaValue={null} statusOptions={READINESS_STATUSES} onSave={save} targetDate={t2} />
              <ReadinessCard title="Video for Pre-loading on ON24" icon={Globe}
                statusField="video_on24_status" statusValue={delivery.video_on24_status}
                etaField={null} etaValue={null} statusOptions={READINESS_STATUSES} onSave={save} targetDate={t2} />
            </div>
            <div className="mt-4 rounded-xl border border-border bg-background p-4 max-w-sm">
              <InlineText label="Content Version" value={delivery.content_version} field="content_version" onSave={save} placeholder="e.g. v1.2" testId="field-content-version" />
            </div>
          </div>

          {/* Readiness Summary */}
          <div className="rounded-xl border border-border bg-background p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">Readiness Summary</h2>
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: 'Content', value: delivery.content_readiness_status },
                { label: 'Lab', value: delivery.lab_readiness_status },
                { label: 'Polling Qs', value: delivery.polling_qs_status },
                { label: 'Video ON24', value: delivery.video_on24_status },
                { label: 'Contract', value: delivery.trainer_contract_status === 'Signed' ? 'Shared' : 'Not Started' },
                { label: 'Content Shared', value: delivery.trainer_content_status === 'Shared' ? 'Shared' : 'Not Started' },
                { label: 'Lab Access', value: delivery.lab_access_status === 'Shared' ? 'Shared' : 'Not Started' },
                { label: 'Pre-Exec Call', value: delivery.pre_execution_call_status === 'Completed' ? 'Shared' : 'Not Started' },
              ].map(item => (
                <div key={item.label} className="text-center p-3 rounded-lg border border-border">
                  <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${readinessColor(item.value)}`}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Execution & Post-Delivery ────────────────────────────── */}
      {activeTab === 'execution' && (
        <div className="space-y-6" data-testid="tab-execution">
          <div className="rounded-xl border border-border bg-background p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">Delivery Status</h2>
            <div className="grid grid-cols-2 gap-4">
              <InlineSelect label="Delivery Status" value={delivery.delivery_status} field="delivery_status" options={DELIVERY_STATUSES} onSave={save} testId="exec-delivery-status" />
              <InlineSelect label="PoE Status" value={delivery.poe_status} field="poe_status" options={POE_STATUSES} onSave={save} testId="exec-poe-status" />
            </div>
          </div>

          <div className="rounded-xl border border-border bg-background p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">Outcomes</h2>
            <div className="grid grid-cols-2 gap-4">
              <InlineText label="Total Attendance" value={delivery.attendance != null ? String(delivery.attendance) : ''} field="attendance"
                onSave={(f, v) => save(f, v ? parseInt(v) : null)} type="number" testId="exec-attendance" />
              <InlineText label="Simulated Lab Consumption" value={delivery.simulated_lab_consumption} field="simulated_lab_consumption" onSave={save} placeholder="e.g. 180 / Asia" testId="exec-lab-consumption" />
            </div>
          </div>

          <div className="rounded-xl border border-border bg-background p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">Feedback</h2>
            <div className="space-y-4">
              <InlineTextarea label="Session Feedback" value={delivery.session_feedback} field="session_feedback" onSave={save} placeholder="Overall session feedback from participants…" testId="exec-session-feedback" />
              <InlineTextarea label="Trainer Feedback" value={delivery.trainer_feedback} field="trainer_feedback" onSave={save} placeholder="Feedback about trainer performance…" testId="exec-trainer-feedback" />
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Financial ────────────────────────────────────────────── */}
      {activeTab === 'financial' && (
        <div className="space-y-6" data-testid="tab-financial">
          <div className="rounded-xl border border-border bg-background p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">Financial Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <InlineText label="PO Number" value={delivery.po_number} field="po_number" onSave={save} placeholder="PO-2026-001" testId="fin-po-number" inherited={inh} />
              <InlineText label="Training Amount ($)" value={delivery.training_amount != null ? String(delivery.training_amount) : ''} field="training_amount"
                onSave={(f, v) => save(f, v ? parseFloat(v) : null)} type="number" testId="fin-training-amount" placeholder="e.g. 5000" />
              <InlineText label="Revised Amount ($)" value={delivery.revised_amount != null ? String(delivery.revised_amount) : ''} field="revised_amount"
                onSave={(f, v) => save(f, v ? parseFloat(v) : null)} type="number" testId="fin-revised-amount" placeholder="If revised" />
            </div>
          </div>

          {/* Financial summary */}
          <div className="rounded-xl border border-border bg-muted/30 p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">Summary</h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">PO Amount</p>
                <p className="text-2xl font-bold text-foreground">
                  {delivery.training_amount != null ? `$${Number(delivery.training_amount).toLocaleString()}` : '—'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Revised Amount</p>
                <p className="text-2xl font-bold text-foreground">
                  {delivery.revised_amount != null ? `$${Number(delivery.revised_amount).toLocaleString()}` : '—'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Billing Amount</p>
                <p className="text-2xl font-bold text-blue-600">
                  {delivery.revised_amount != null
                    ? `$${Number(delivery.revised_amount).toLocaleString()}`
                    : delivery.training_amount != null
                    ? `$${Number(delivery.training_amount).toLocaleString()}`
                    : '—'}
                </p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
              Contract: <span className={`font-medium ${delivery.trainer_contract_status === 'Signed' ? 'text-emerald-600' : 'text-amber-600'}`}>{delivery.trainer_contract_status}</span>
              {' · '} PoE: <span className={`font-medium ${delivery.poe_status === 'Submitted' ? 'text-emerald-600' : 'text-amber-600'}`}>{delivery.poe_status}</span>
            </div>
          </div>
        </div>
      )}

      <ReportModal open={showReport} onClose={() => setShowReport(false)}
        deliveryId={id} deliveryTitle={delivery.course_title} />
      <ContentItemLinkDialog open={contentItemDialogOpen} onOpenChange={setContentItemDialogOpen} onSelect={handleLinkContentItem} />
    </div>
  );
}
